import { useLazyQuery, useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '@gsa/afp-shared-ui-utils';
import { groupBy, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { getErrorName } from '../../services/data-store';
import { GET_STANDARD_ITEMS_WITHOUT_SOLICITATION } from '../../services/data-store.gql';
import { pageErrorMessage } from '../../utilities/error-handling';
import { RoleIDs } from '../../utilities/types/role-types';
import { FVS_COMMENT_STATUS } from '../../utilities/constants';
import {
  COUNT_OPEN_COMMENTING_PERIODS,
  GET_APPLICABLE_YEARS,
  GET_FVS_OPTIONS_FOR_YEAR,
  GET_VEHICLES_STANDARDS,
  PUBLISH_FVS,
} from './fvs.gql';

export const FvsContext = createContext({});

const FVS_ERROR_FALLBACK =
  'An unexpected error occurred. Please try again later.';

const initialState = {
  fvsMessages: [],
  applicableFvsYears: [],
  vehicleClassificationOptions: {},
  selectedYear: null,
  selectedOptions: {
    year: null,
    fedStandardCode: '',
    vehicleGroup: '',
    vehicleType: '',
    standardItemNumber: null,
  },
  vehicleStandard: null,
  commentingPeriodCount: null,
  commentingPeriodsData: {},
  standardItems: [],
  standardItemsAsOptions: [],
  standardItem: null,
  isCommentingAllowed4Fvs4User: false,
  fvsError: null,
  equipmentCodeQueryString: '',
};

const FvsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FVS_MESSAGES': {
      return { ...state, fvsMessages: action.payload };
    }
    case 'SET_FVS_YEARS': {
      return { ...state, applicableFvsYears: action.payload };
    }
    case 'SET_EQUIPMENT_CODE_QUERY_STRING': {
      return { ...state, equipmentCodeQueryString: action.payload };
    }
    case 'SET_FVS_OPTIONS': {
      return { ...state, vehicleClassificationOptions: action.payload };
    }
    case 'SET_SELECTED_YEAR': {
      return { ...state, selectedYear: action.payload };
    }
    case 'SET_SELECTED_OPTIONS': {
      return { ...state, selectedOptions: action.payload };
    }
    case 'SET_VEHICLE_STANDARDS': {
      return { ...state, vehicleStandard: { ...action.payload } };
    }
    case 'SET_COMMENTING_PERIOD_COUNT': {
      return { ...state, commentingPeriodCount: action.payload };
    }
    case 'SET_COMMENTING_PERIOD_DATA': {
      return { ...state, commentingPeriodsData: action.payload };
    }
    case 'SET_STANDARD_ITEMS': {
      return {
        ...state,
        standardItems: action.payload.standardItems,
        standardItemsAsOptions: action.payload.standardItemsAsOptions,
      };
    }
    case 'SET_SELECTED_STANDARD_ITEM': {
      if (action.payload === null) {
        return {
          ...state,
          standardItem: null,
        };
      }
      const si = state.standardItems.find(
        (s) => s.standardItemNumber === action.payload,
      );
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          standardItemNumber: action.payload,
        },
        standardItem: si,
      };
    }
    case 'SET_SELECTED_STANDARD_ITEM_OBJ': {
      return {
        ...state,

        standardItem: action.payload,
      };
    }
    case 'SET_COMMENTING_ALLOWED': {
      return { ...state, isCommentingAllowed4Fvs4User: action.payload };
    }
    default:
      return state;
  }
};

function FvsProvider({ children, ...props }) {
  const location = useLocation();
  const { currentUser } = useCurrentUser();

  const [state, dispatch] = useReducer(FvsReducer, initialState, () => {
    const params = new URLSearchParams(location.search);
    const filterYear = params.get('filter-year');
    return {
      ...initialState,
      selectedYear: filterYear
        ? Number.parseInt(filterYear, 10)
        : initialState.selectedYear,
    };
  });

  const setFvsContextData = (type, payload) => {
    dispatch({
      type,
      payload,
    });
  };

  const addOrReplaceFvsMessageById = (message) => {
    const existingMessages = [...state.fvsMessages];
    const splicedArray = existingMessages.filter((m) => m.id !== message.id);
    setFvsContextData('SET_FVS_MESSAGES', [...splicedArray, message]);
  };

  const removeFvsMessageById = (id) => {
    const existingMessages = [...state.fvsMessages];
    const splicedArray = existingMessages.filter((m) => m.id !== id);
    setFvsContextData('SET_FVS_MESSAGES', [...splicedArray]);
  };

  const clearAllMessages = () => {
    setFvsContextData('SET_FVS_MESSAGES', []);
  };

  const setFvsError = (error) => {
    // null when the global handler already shows the system error for this
    // one; otherwise the generic banner is taken down so only this one shows
    const shown = pageErrorMessage(error, error?.message || FVS_ERROR_FALLBACK);
    if (shown === null) return;

    const message = {
      id: getErrorName(error) || 'FVS_ERROR',
      message: shown,
      type: 'error',
    };
    addOrReplaceFvsMessageById(message);
  };

  const [
    getApplicableFvsYears,
    { loading: isApplicableFvsYearsLoading },
  ] = useLazyQuery(GET_APPLICABLE_YEARS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (requestError) => {
      setFvsError(requestError);
    },
    onCompleted: (responseData) => {
      if (responseData?.getYearsForFVS) {
        const years = responseData.getYearsForFVS;
        setFvsContextData('SET_FVS_YEARS', [...years]);

        // Set the default selected year if not already set (e.g., from URL params).
        // Fall back to the most recent year when the backend does not flag any
        // year as default for the current role (e.g., Admin/CO when next-year
        // FVS status is not in the publishable list); otherwise the filter
        // panel would never render for those users.
        if (!state.selectedYear) {
          const yearToSelect =
            years.find((year) => year.isDefault === true) || years[0];
          if (yearToSelect) {
            setFvsContextData(
              'SET_SELECTED_YEAR',
              Number.parseInt(yearToSelect.value, 10),
            );
          }
        }
      }
    },
  });

  const sortByLabel = (items) => {
    return items?.sort((a, b) => {
      if (a.label > b.label) {
        return 1;
      }
      if (a.label < b.label) {
        return -1;
      }
      return 0;
    });
  };

  const getStoreTaggedVehicleTypes = (vehicleTypes) => {
    return vehicleTypes.filter((vt) => {
      return vt.tags?.value?.includes('STORE');
    });
  };


  const filterByStoreTag = (data) => {
    const { fedStandardCode, vehicleGroup, vehicleType } = data;

    // Step 1: Filter vehicleType items with STORE tag
    const filteredVehicleTypes = vehicleType.filter((vt) =>
      vt.tags?.value?.includes('STORE'),
    );

    // Step 2: Find all unique vehicleGroup parents of those vehicleTypes
    const vehicleGroupParents = new Set(
      filteredVehicleTypes.map((vt) => vt.parent),
    );

    // Step 3: Filter vehicleGroups that match those parents
    const filteredVehicleGroups = vehicleGroup.filter((vg) =>
      vehicleGroupParents.has(vg.value),
    );

    // Step 4: Find all unique fedStandardCodes parents of those vehicleGroups
    const fedStandardParents = new Set(
      filteredVehicleGroups.map((vg) => vg.parent),
    );

    // Step 5: Filter fedStandardCodes that match those parents
    const filteredFedStandardCodes = fedStandardCode.filter((fsc) =>
      fedStandardParents.has(fsc.value),
    );

    // Step 6: Return the filtered hierarchy
    return {
      fedStandardCode: filteredFedStandardCodes,
      vehicleGroup: filteredVehicleGroups,
      vehicleType: filteredVehicleTypes,
    };
  }


  const [
    getFvsOptionsForYear,
    { loading: isFVSOptionsForYearLoading },
  ] = useLazyQuery(GET_FVS_OPTIONS_FOR_YEAR, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (requestError) => {
      setFvsError(requestError);
    },
    onCompleted: (responseData) => {
      if (responseData?.getVehicleClassificationFVS) {
        const allOptionsGrouped = groupBy(
          responseData.getVehicleClassificationFVS,
          'uniqueKey',
        );
        const allOptions = {};

        if (allOptionsGrouped?.vehicleGroup) {
          const sortedVehGrps = sortByLabel(allOptionsGrouped.vehicleGroup);
          allOptions.vehicleGroup = sortedVehGrps;
        }
        if (allOptionsGrouped?.vehicleType) {
          const sortedVehTypes = sortByLabel(allOptionsGrouped.vehicleType);
          allOptions.vehicleType = getStoreTaggedVehicleTypes(sortedVehTypes);
        }
        if (allOptionsGrouped?.fedStandardCodes) {
          allOptions.fedStandardCode = allOptionsGrouped.fedStandardCodes;
        }

        const filteredOptions = filterByStoreTag(allOptions);

        setFvsContextData('SET_FVS_OPTIONS', filteredOptions);
      }
    },
  });

  const [
    getVehicleStandards,
    { loading: vehicleStandardsLoading },
  ] = useLazyQuery(GET_VEHICLES_STANDARDS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (requestError) => {
      setFvsError(requestError);
    },
    onCompleted: (responseData) => {
      if (responseData?.getFederalStandards) {
        if (
          !responseData.getFederalStandards?.rows ||
          responseData.getFederalStandards?.rows.length < 1
        ) {
          const message = {
            id: 'FVS_ERROR',
            message: 'Not a valid Federal Standard Code.',
            type: 'error',
          };
          addOrReplaceFvsMessageById(message);
          setFvsContextData('SET_VEHICLE_STANDARDS', null);
          return;
        }
        setFvsContextData(
          'SET_VEHICLE_STANDARDS',
          responseData.getFederalStandards?.rows[0],
        );
        removeFvsMessageById('FVS_ERROR');
        if (!state?.selectedOptions.fedStandardCode) {
          setFvsContextData('SET_SELECTED_OPTIONS', {
            ...state?.selectedOptions,
            fedStandardCode:
              responseData.getFederalStandards?.rows[0].fedStandardCode,
          });
        }
        const roles = currentUser.roles.map(({ id }) => id);
        const fvs = responseData?.getFederalStandards?.rows[0];
        const fvsYear = fvs?.year;
        if (fvsYear > new Date().getFullYear()) {
          const isEngineerOrSenior =
            roles.includes(RoleIDs.Engineer) ||
            roles.includes(RoleIDs.SeniorEngineer);
          const isPSO = roles.includes(RoleIDs.PSO);

          // AFP-229609 PR 2 - each audience has its own commenting state, so a
          // role is allowed to comment when its own period is open. Engineers
          // and senior engineers may comment while either period is open.
          const isVsOpen = fvs?.vsCommentStatus === FVS_COMMENT_STATUS.Open;
          const isPublicOpen =
            fvs?.publicCommentStatus === FVS_COMMENT_STATUS.Open;

          if (
            (isEngineerOrSenior && (isVsOpen || isPublicOpen)) ||
            (isPSO && isVsOpen) ||
            (!isEngineerOrSenior && !isPSO && isPublicOpen)
          ) {
            setFvsContextData('SET_COMMENTING_ALLOWED', true);
          }
        }
      }
    },
  });

  const [getStandardItems, { loading: isSILoading }] = useLazyQuery(
    GET_STANDARD_ITEMS_WITHOUT_SOLICITATION,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError: (requestError) => {
        setFvsError(requestError);
      },
      onCompleted: (responseData) => {
        if (responseData?.getStandardItems) {
          const standardItemsOptions = responseData?.getStandardItems?.rows.map(
            (option) => ({
              value: option.standardItemNumber,
              label: `${option.standardItemNumber} - ${option.title}`,
            }),
          );
          const options = [...standardItemsOptions];
          setFvsContextData('SET_STANDARD_ITEMS', {
            standardItems: responseData?.getStandardItems?.rows,
            standardItemsAsOptions: options,
          });
        }
      },
    },
  );

  const [publishFvs] = useMutation(PUBLISH_FVS, {
    onError: (requestError) => {
      setFvsError(requestError);
    },
    onCompleted: () => {
      getVehicleStandards({
        variables: {
          filters: {
            operator: 'AND',
            value: [
              {
                operator: 'EQ',
                key: 'id',
                value: state.vehicleStandard.id,
              },
            ],
          },
          limit: 1,
          offset: 0,
          order: [['fedStandardCode', 'ASC']],
        },
      });

      const message = {
        id: 'FVS_STATUS',
        message: `Federal standard ${state.vehicleStandard.fedStandardCode} published successfully.`,
        type: 'success',
        closeable: true,
      };
      addOrReplaceFvsMessageById(message);
    },
  });

  const [
    getOpenCommentingPeriodCount,
    { loading: isCommentingPeriodDataLoading },
  ] = useLazyQuery(COUNT_OPEN_COMMENTING_PERIODS, {
    fetchPolicy: 'network-only',
    onError: (requestError) => {
      setFvsError(requestError);
    },
    onCompleted: (periodResponse) => {
      setFvsContextData(
        'SET_COMMENTING_PERIOD_DATA',
        periodResponse.getCountOpenCommentingPeriods,
      );
    },
  });

  useEffect(() => {
    getApplicableFvsYears();
  }, []);

  useEffect(() => {
    if (state?.selectedYear) {
      getFvsOptionsForYear({
        variables: {
          year: Number.parseInt(state.selectedYear, 10),
          includeOnlyCommentable: false,
        },
      });
      getOpenCommentingPeriodCount();
    }
  }, [state?.selectedYear]);

  useEffect(() => {
    const isInDocPreviewPage = location?.pathname?.includes('/preview');
    if (isInDocPreviewPage) return;
    setFvsContextData('SET_COMMENTING_ALLOWED', false);
    if (
      state?.selectedYear &&
      state?.selectedOptions.fedStandardCode
    ) {
      getVehicleStandards({
        variables: {
          filters: {
            operator: 'AND',
            value: [
              {
                operator: 'EQ',
                key: 'year',
                value: Number.parseInt(state.selectedYear, 10),
              },
              {
                operator: 'EQ',
                key: 'fedStandardCode',
                value: state.selectedOptions.fedStandardCode,
              },
            ],
          },
          limit: 1,
          offset: 0,
          order: [['fedStandardCode', 'ASC']],
        },
      });
    } else {
      setFvsContextData('SET_VEHICLE_STANDARDS', null);
    }
  }, [state?.selectedYear, state?.selectedOptions.fedStandardCode]);

  useEffect(() => {
    // Only fetch if at least one filter is selected (fedStandardCode, vehicleGroup, or vehicleType)
    const hasAnyFilter =
      !isEmpty(state?.selectedOptions?.fedStandardCode) ||
      !isEmpty(state?.selectedOptions?.vehicleGroup) ||
      !isEmpty(state?.selectedOptions?.vehicleType);

    if (!hasAnyFilter) {
      return;
    }

    const filters = {
      operator: 'AND',
      value: [
        { operator: 'EQ', key: 'year', value: state.selectedYear },
        { operator: 'EQ', key: 'deletedAt', value: null },
        { operator: 'EQ', key: 'isActive', value: 1 },
      ],
    };

    if (!isEmpty(state?.selectedOptions?.fedStandardCode)) {
      filters.value.push({
        operator: 'EQ',
        key: 'fedStandardCode',
        value: state.selectedOptions.fedStandardCode,
      });
    }

    if (!isEmpty(state?.selectedOptions?.vehicleGroup)) {
      filters.value.push({
        operator: 'EQ',
        key: '$vehicleTypeCode.parent_code$',
        value: state.selectedOptions.vehicleGroup,
      });
    }

    if (!isEmpty(state?.selectedOptions?.vehicleType)) {
      filters.value.push({
        operator: 'EQ',
        key: 'vehicleType',
        value: state.selectedOptions.vehicleType,
      });
    }

    getStandardItems({
      variables: {
        filters,
        limit: 1000,
        offset: 0,
        order: [['standardItemNumber', 'ASC']],
      },
    });
  }, [
    state?.selectedYear,
    state?.selectedOptions?.fedStandardCode,
    state?.selectedOptions?.vehicleGroup,
    state?.selectedOptions?.vehicleType,
  ]);

  return (
    <FvsContext.Provider
      value={{
        ...state,
        addOrReplaceFvsMessageById,
        removeFvsMessageById,
        setFvsContextData,
        getVehicleStandards,
        vehicleStandardsLoading,
        isSILoading,
        publishFvs,
        getOpenCommentingPeriodCount,
        setFvsError,
        clearAllMessages,
        isCommentingPeriodDataLoading,
        isApplicableFvsYearsLoading,
        isFVSOptionsForYearLoading,
        ...props,
      }}
    >
      {children}
    </FvsContext.Provider>
  );
}

export default FvsProvider;

FvsProvider.propTypes = {
  children: PropTypes.element.isRequired,
};

export const useFederalStandards = () => useContext(FvsContext);